import React, { useState } from "react";
import { Stack, Typography, Button } from "@mui/material";
import moment from "moment";
import ListItemSection from "../../Widgets/ListItemSection";
import ViewAuthorizationDialog from "./ViewAuthorizationDialog";

interface ViewFieldsProps {
  authorizationCodes: any[];
  userPermissions: any;
  selectedTab: "ongoing" | "secondary";
}

const ViewFields: React.FC<ViewFieldsProps> = ({ authorizationCodes, selectedTab }) => {
  const [viewMore, setViewMore] = useState(false);
  const [viewAuthorizationOpen, setViewAuthorizationOpen] = useState(false);
  const [authorizationInViewMode, setAuthorizationInViewMode] = useState<any>({});

  const mapAuthTypeToDisplay = (authType: string) => {
    if (!authType) {
      return "----";
    } else if (authType === "pre_auth") {
      return "Pre-Authorization";
    } else if (authType === "auth") {
      return "Ongoing Authorization";
    } else if (authType === "re_auth") {
      return "Re-Authorization";
    } else {
      return "Secondary Authorization";
    }
  };

  const renderListSubtitle = (code: any) =>
    `${moment(code.start_date).utc().format("MM/DD/YYYY")} - ${moment(code.end_date).utc().format("MM/DD/YYYY")} | ${code.authorization_code} ${code.clinician_name ? `| ${code.clinician_name}` : ""}`;

  const onOpenViewAuthorization = (index: number) => {
    setViewAuthorizationOpen(true);
    const selectedAuthorization = authorizationCodes[index];
    setAuthorizationInViewMode(selectedAuthorization);
  };

  const onCloseViewAuthorization = () => {
    setViewAuthorizationOpen(false);
    setAuthorizationInViewMode({});
  };

  const isSecondaryTab = selectedTab === "secondary";

  const filteredCodes = authorizationCodes
    .map((code, index) => ({ ...code, originalIndex: index }))
    .filter((code) =>
      isSecondaryTab
        ? code.authorization_type === "secondary_auth"
        : code.authorization_type !== "secondary_auth"
    );

  const displayedCodes = viewMore ? filteredCodes : filteredCodes.slice(0, 3);
  const additional = filteredCodes.length - displayedCodes.length;
  const emptyLabel = isSecondaryTab ? "No secondary authorizations" : "No ongoing authorizations";

  return (
    <>
      <Stack width="100%" gap="12px">
        <Typography variant="subtitle1">
          {isSecondaryTab ? "Secondary authorizations" : "Ongoing authorizations"}
        </Typography>
        {displayedCodes.length > 0 ? (
          displayedCodes.map((code) => (
            <ListItemSection
              key={code.originalIndex}
              title={mapAuthTypeToDisplay(code.authorization_type)}
              subtitle={renderListSubtitle(code)}
              isPrimary={false}
              editMode={false}
              onClick={() => onOpenViewAuthorization(code.originalIndex)}
              customEditComponent={null}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            {emptyLabel}
          </Typography>
        )}
        {filteredCodes.length > 3 && (
          <Button
            onClick={() => setViewMore((current) => !current)}
            variant="text"
            sx={{ width: "fit-content" }}
          >
            {viewMore ? "View Less" : `View ${additional} More`}
          </Button>
        )}
      </Stack>

      {viewAuthorizationOpen && (
        <ViewAuthorizationDialog
          open={viewAuthorizationOpen}
          onClose={onCloseViewAuthorization}
          data={authorizationInViewMode}
        />
      )}
    </>
  );
};

export default ViewFields;
